/**
 * Admin sign-in: phone OTP, with the reCAPTCHA verifier the web SDK requires.
 *
 * ── Why reCAPTCHA is here and not in the Android apps ──────────────────────
 *
 * `signInWithPhoneNumber` on the web will not send an SMS without an
 * `ApplicationVerifier`. Android has an equivalent — Play Integrity, keyed on
 * the app's signing certificate — but it is invisible because the SDK handles
 * it. On the web it is explicit, so this screen owns a verifier's lifecycle.
 *
 * Size `invisible`: the challenge only appears if Google thinks the traffic is
 * suspicious. For a handful of admins signing in from known machines, that is
 * usually never, so the ordinary path stays one field and one code.
 *
 * ── The trap this file is written around ───────────────────────────────────
 *
 * A `RecaptchaVerifier` is single-use in practice. Once its token is spent —
 * or once a send fails — reusing the same instance produces
 * `auth/internal-error` or a silent no-op on the next attempt, which reads to
 * the user as "the button does nothing". So the verifier is created per send
 * and **cleared afterwards**, on both the success and failure paths.
 *
 * ── Same UID as the Flutter admin app ──────────────────────────────────────
 *
 * Signing in with the same phone number yields the same Firebase UID, so the
 * admin claim `scripts/provision-admin.mjs` already granted applies here with
 * no re-provisioning. That is the whole reason phone auth was chosen over
 * email/password: a second credential would mean a second UID and a second
 * provisioning story.
 */
import { useRef, useState } from 'react'
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { AppFailure } from '../lib/failure'

/** Mirrors `normalisePhone` in `functions/src/common.ts`. */
function normalisePhone(input: string): string | null {
  const digits = input.replace(/\D/g, '')
  const local =
    digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits
  return /^[6-9]\d{9}$/.test(local) ? `+91${local}` : null
}

export default function SignIn() {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const verifierRef = useRef<RecaptchaVerifier | null>(null)

  /** Tears the verifier down. Safe to call when there is nothing to clear. */
  function clearVerifier() {
    try {
      verifierRef.current?.clear()
    } catch {
      // Already gone. Nothing to do, and nothing worth telling anyone.
    }
    verifierRef.current = null
  }

  async function sendCode(event: React.FormEvent) {
    event.preventDefault()
    const e164 = normalisePhone(phone)
    if (!e164) {
      setError('Enter a 10-digit Indian mobile number.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      clearVerifier()
      verifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-host', {
        size: 'invisible',
      })
      const result = await signInWithPhoneNumber(auth, e164, verifierRef.current)
      setConfirmation(result)
      // Spent. A second send builds a fresh one.
      clearVerifier()
    } catch (caught) {
      clearVerifier()
      setError(describeAuthError(caught))
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(event: React.FormEvent) {
    event.preventDefault()
    if (!confirmation) return
    if (!/^\d{6}$/.test(code.trim())) {
      setError('The code is six digits.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      await confirmation.confirm(code.trim())
      // Nothing to do on success: `SessionProvider` sees the auth change and
      // the router moves. No screen navigates after sign-in — the same rule
      // the Flutter app follows, and what lets a token refresh reroute on its
      // own (CLAUDE.md §12).
    } catch (caught) {
      setError(describeAuthError(caught))
      setBusy(false)
    }
  }

  return (
    <div className="signin-wrap">
      <div className="signin-card">
        {/* The same mark the rail carries, so the console is recognisable
            before anyone is signed in to see the rail. */}
        <span
          className="nav-mark"
          aria-hidden="true"
          style={{ width: 38, height: 38, marginBottom: 'var(--sp-5)' }}
        >
          J
        </span>
        <h1 style={{ marginBottom: 'var(--sp-2)' }}>Jaagur Admin</h1>
        <p style={{ color: 'var(--ink-faint)', marginBottom: 'var(--sp-6)' }}>
          {confirmation
            ? `Enter the six-digit code sent to ${normalisePhone(phone)}.`
            : 'Sign in with the phone number your admin account was created with.'}
        </p>

        {error && (
          <div className="banner banner-error" style={{ marginBottom: 'var(--sp-4)' }}>
            <span>{error}</span>
          </div>
        )}

        {!confirmation ? (
          <form onSubmit={sendCode} className="stack">
            <div className="field">
              <label htmlFor="phone">Mobile number</label>
              <input
                id="phone"
                className="input"
                type="tel"
                autoComplete="tel"
                inputMode="numeric"
                placeholder="98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
                autoFocus
              />
              <span className="hint">Indian mobile, with or without +91.</span>
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="stack">
            <div className="field">
              <label htmlFor="code">Six-digit code</label>
              <input
                id="code"
                className="input mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmation(null)
                setCode('')
                setError(null)
              }}
            >
              Use a different number
            </button>
          </form>
        )}

        {/* The verifier mounts here. Invisible, but it must exist in the DOM. */}
        <div id="recaptcha-host" />
      </div>
    </div>
  )
}

/**
 * Auth codes, phrased for the person reading them.
 *
 * `invalid-app-credential` is called out by name because it is the one an
 * admin cannot fix and will otherwise read as "wrong number": on the web it
 * means the domain is not authorised in Firebase Auth, exactly as its Android
 * twin means the signing certificate is not registered. The Flutter app
 * carries the same special case for the same reason.
 */
function describeAuthError(error: unknown): string {
  const code = String((error as { code?: unknown })?.code ?? '')

  switch (code) {
    case 'auth/invalid-phone-number':
      return 'That is not a valid phone number.'
    case 'auth/invalid-verification-code':
      return 'That code is not right. Check it and try again.'
    case 'auth/code-expired':
      return 'That code has expired. Ask for a new one.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'auth/invalid-app-credential':
    case 'auth/captcha-check-failed':
      return (
        'This site is not authorised to send OTPs. Its domain needs adding ' +
        'to Firebase Authentication → Settings → Authorised domains.'
      )
    case 'auth/operation-not-allowed':
      return 'Phone sign-in is not enabled on this Firebase project.'
    default:
      return AppFailure.from(error).message
  }
}
